import { useEffect, useState, type FormEvent } from "react";
import { motion } from "framer-motion";
import {
  getProfileNameValidationError,
  MAX_PROFILE_NAME_LENGTH,
  sanitizeProfileName
} from "../game/profileStorage";

type ProfileNameModalProps = {
  open: boolean;
  initialValue: string;
  onClose: () => void;
  onSave: (profileName: string) => void;
};

export function ProfileNameModal({
  open,
  initialValue,
  onClose,
  onSave
}: ProfileNameModalProps) {
  const [draftProfileName, setDraftProfileName] = useState(initialValue);
  const validationError = getProfileNameValidationError(draftProfileName);

  useEffect(() => {
    if (!open) {
      return;
    }

    setDraftProfileName(initialValue);
  }, [initialValue, open]);

  if (!open) {
    return null;
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (getProfileNameValidationError(draftProfileName) !== null) {
      return;
    }

    onSave(draftProfileName);
    onClose();
  };

  return (
    <motion.div
      className="profile-name-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Change profile name"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.18, ease: "easeOut" }}
      onClick={onClose}
    >
      <motion.div
        className="profile-name-modal-card panel"
        initial={{ opacity: 0, scale: 0.94, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.18, ease: "easeOut" }}
        onClick={(event) => event.stopPropagation()}
      >
        <h3 className="profile-name-modal-title">Change Name</h3>
        <p className="profile-name-modal-copy">
          Set a profile name for future leaderboard saves.
        </p>

        <form className="profile-name-modal-form" onSubmit={handleSubmit}>
          <input
            type="text"
            className="profile-name-modal-input"
            value={draftProfileName}
            maxLength={MAX_PROFILE_NAME_LENGTH}
            aria-invalid={validationError !== null}
            placeholder="You"
            autoCapitalize="words"
            autoComplete="nickname"
            autoFocus
            onChange={(event) => setDraftProfileName(sanitizeProfileName(event.target.value))}
          />
          {validationError !== null ? (
            <p className="profile-name-modal-copy profile-name-modal-error" role="alert">
              {validationError}
            </p>
          ) : null}

          <div className="profile-name-modal-actions">
            <button
              type="button"
              className="profile-name-modal-button profile-name-modal-button-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="profile-name-modal-button profile-name-modal-button-primary"
              disabled={validationError !== null}
            >
              Save
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
